import { Fragment, FunctionComponent, MouseEventHandler, ReactNode, SVGAttributes } from 'react';
import { Link } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import cn from 'classnames';
import { t } from '@/utils/i18n';
import { IconComponent } from '@/components/common/icons/BaseIcon';
import { Close } from '@/components/common/icons/Close';

interface HeaderProps {
  text: string;
  onClose: () => void;
}

export const MenuHeader: FunctionComponent<HeaderProps> = (props) => (
  <div className="menu__header">
    <span className="menu__header-text">{props.text}</span>
    <button className="menu__header-button" type="button" onClick={props.onClose}>
      {t('general.close')}
      <Close />
    </button>
  </div>
);

interface MenuButtonProps {
  onClick?: MouseEventHandler;
  disabled?: boolean;
  active?: boolean;
  title?: string;
  text?: string;
  icon: IconComponent;
  className?: string;
  iconProps?: SVGAttributes<SVGElement>;
  to?: string;
  onDisabledClick?: () => void;
  children?: ReactNode;
}

export const MenuButton: FunctionComponent<MenuButtonProps> = ({
  active,
  className,
  onClick,
  disabled,
  title,
  text,
  icon: Icon,
  iconProps,
  to,
  onDisabledClick,
  children,
}) => {
  const fullClassName = cn(
    'menu__button',
    {
      'menu__button--active': active,
      'menu__button--disabled': disabled,
    },
    className
  );

  const childrenElement = (
    <Fragment>
      <div style={{ position: 'relative' }}>
        <Icon width="2em" height="2em" {...iconProps} />
      </div>
      <span>{text}</span>
      {children}
    </Fragment>
  );

  if (disabled) {
    return (
      // hint for users that click on the button despite being a non-interactive element
      // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
      <span className={fullClassName} onClick={onDisabledClick}>
        {childrenElement}
      </span>
    );
  }

  if (to) {
    return (
      <Link to={to} className={fullClassName} onClick={onClick}>
        {childrenElement}
      </Link>
    );
  }

  return (
    <button type="button" title={title} className={fullClassName} onClick={onClick}>
      {childrenElement}
    </button>
  );
};

interface SubMenuProps {
  children: ReactNode;
  open: boolean;
  role?: string;
}

export const SubMenu: FunctionComponent<SubMenuProps> = ({ open, children, role }) => (
  <CSSTransition in={open} timeout={200} mountOnEnter unmountOnExit appear>
    <div className="sub-menu" role={role}>
      {children}
    </div>
  </CSSTransition>
);
